import { createTRPCReact } from "@trpc/react-query";
import { createTRPCProxyClient, httpBatchLink } from "@trpc/client";
import type { AppRouter } from "../../../server/src/routers/_app";
import { authClient, buildAuthHeaders, getBackendUrl } from "./authClient";

export const trpc = createTRPCReact<AppRouter>();

/**
 * Le jeton d'accès expire au bout d'1 h. Avant, tout 401 renvoyait sèchement
 * sur /login : le staff était déconnecté toutes les heures alors que son
 * refresh token (30 j) restait valide. On tente désormais le rafraîchissement
 * silencieux, et on ne redirige que si le serveur refuse explicitement — jamais
 * sur une panne réseau.
 */
const onSessionLost = () => {
  authClient.markLoggedOut();
  localStorage.removeItem("admin_token");
  localStorage.removeItem("admin_user");
  if (typeof window !== "undefined" && !window.location.pathname.includes("/login")) {
    window.location.href = "/login";
  }
};

const refreshingFetch = authClient.createAuthedFetch({
  buildHeaders: buildAuthHeaders,
  onSessionLost,
});

const authedFetch: typeof fetch = async (input, options = {}) => {
  try {
    return await refreshingFetch(input, options);
  } catch (error) {
    if (error instanceof TypeError && error.message.includes("fetch")) {
      const backendUrl = getBackendUrl();
      throw new Error(
        `Le serveur backend n'est pas accessible à l'adresse ${backendUrl}. ` +
        `Vérifiez que le backend est démarré et que la variable VITE_BACKEND_URL est correctement configurée.`
      );
    }
    throw error;
  }
};

export const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: `${getBackendUrl()}/trpc`,
      headers: buildAuthHeaders,
      fetch: authedFetch,
    }),
  ],
});

// Vanilla imperative client — used by AuthContext for logout etc.
export const trpcVanilla = createTRPCProxyClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${getBackendUrl()}/trpc`,
      headers: buildAuthHeaders,
      fetch: authedFetch,
    }),
  ],
});
